import { initializeApp } from "firebase/app"
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions"
import {
	getStorage,
	ref,
	connectStorageEmulator,
	getDownloadURL,
	uploadBytes,
	uploadBytesResumable,
} from "firebase/storage"
import { connectAuthEmulator, getAuth, signOut } from "firebase/auth"
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check"
import { getDatabase, connectDatabaseEmulator } from "firebase/database"

const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
	projectId: process.env.REACT_APP_FIREBASE_PROJECTID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGEBUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGESENTERID,
	appId: process.env.REACT_APP_FIREBASE_APPID,
	measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENTID,
	databaseURL: process.env.REACT_APP_RTDB_URL,
}

if ((!process.env.NODE_ENV || process.env.NODE_ENV === "development") && process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
	window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN
}

export const app = initializeApp(firebaseConfig)

initializeAppCheck(app, {
	provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),

	// Optional argument. If true, the SDK automatically refreshes App Check
	// tokens as needed.
	isTokenAutoRefreshEnabled: true,
})

export const auth = getAuth(app)
const functions = getFunctions(app, process.env.REACT_APP_BULLCAR_API_ENDPOINT)
let functionsV2
const storage = getStorage(app)
export const db = getDatabase(app)

if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
	functionsV2 = getFunctions(app, "europe-west1")
	connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true })
	connectFunctionsEmulator(functions, "localhost", 5001)
	connectFunctionsEmulator(functionsV2, "localhost", 5001)
	connectStorageEmulator(storage, "localhost", 9199)
	connectDatabaseEmulator(db, "localhost", 9000)
} else {
	functionsV2 = getFunctions(app, process.env.REACT_APP_BULLCAR_API_ENDPOINT_V2)
}

export const logout = async () => {
	await signOut(auth)
}

export const uploadMedia = async (fileName, file, owner, postId, contentType) => {
	const storageRef = ref(storage, fileName)
	const snapshot = await uploadBytes(storageRef, file, {
		contentType: contentType,
		customMetadata: { owner: owner, postId: postId },
	})
	return snapshot.metadata
}

export const uploadMediaWithProgress = async (fileName, file, owner, postId, contentType, onRunning, onComplete) => {
	const storageRef = ref(storage, fileName)
	const metadata = {
		contentType: contentType,
		customMetadata: { owner: owner, postId: postId },
	}
	const uploadTask = uploadBytesResumable(storageRef, file, metadata)

	uploadTask.on(
		"state_changed",
		(snapshot) => {
			const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
			//console.log('Upload is ' + progress + '% done');
			onRunning(progress)
		},
		(error) => {
			console.log(error)
		},
		() => {
			getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
				//console.log('File available at', downloadURL);
				onComplete(downloadURL)
			})
		}
	)
	//return snapshot.metadata
}

export const getFileUrl = async (fileName) => {
	const storageRef = ref(storage, fileName)
	return await getDownloadURL(storageRef)
}

export const createPostV2 = httpsCallable(functionsV2, "posts-createPostV2", {
	limitedUseAppCheckTokens: true,
})
export const extUpdatePostV2 = httpsCallable(functionsV2, "posts-extUpdatePostV2")
export const getPostDataForEditV2 = httpsCallable(functionsV2, "posts-getPostDataForEditV2")

export const extDoSearchPriceListV2 = httpsCallable(functionsV2, "search-extDoSearchPriceListV2")

export const adminGetQuestionsForApprovalV2 = httpsCallable(functionsV2, "adminposts-adminGetQuestionsForApprovalV2")
export const adminApproveDenyQuestionV2 = httpsCallable(functionsV2, "adminposts-adminApproveDenyQuestionV2")
export const adminGetPostsV2 = httpsCallable(functionsV2, "adminposts-adminGetPostsV2")
export const adminPublishDocumentsV2 = httpsCallable(functionsV2, "adminposts-adminPublishDocumentsV2")

export const adminExecuteJ4TransactionV2 = httpsCallable(functionsV2, "creditcard-adminExecuteJ4TransactionV2")
export const adminExecuteCreditTransactionV2 = httpsCallable(functionsV2, "creditcard-adminExecuteCreditTransactionV2")

export const adminGetUsersV2 = httpsCallable(functionsV2, "admincrm-adminGetUsersV2")
export const adminGetUserProfileV2 = httpsCallable(functionsV2, "admincrm-adminGetUserProfileV2")
export const adminUpdateUserProfileV2 = httpsCallable(functionsV2, "admincrm-adminUpdateUserProfileV2")
export const adminFindUserV2 = httpsCallable(functionsV2, "admincrm-adminFindUserV2")

export const adminGetUserCountV2 = httpsCallable(functionsV2, "admindashboard-adminGetUserCountV2")
export const adminGetPostsCountByStatusV2 = httpsCallable(functionsV2, "admindashboard-adminGetPostsCountByStatusV2")
export const adminGetBidsCountV2 = httpsCallable(functionsV2, "admindashboard-adminGetBidsCountV2")
export const adminGetDealsCountV2 = httpsCallable(functionsV2, "admindashboard-adminGetDealsCountV2")
export const adminGetQuestionsCountV2 = httpsCallable(functionsV2, "admindashboard-adminGetQuestionsCountV2")
export const adminGetRevenueV2 = httpsCallable(functionsV2, "admindashboard-adminGetRevenueV2")

export const testingCreateUsersV2 = httpsCallable(functionsV2, "testing-testingCreateUsersV2")
export const testingCleanEsEngineV2 = httpsCallable(functionsV2, "testing-testingCleanEsEngineV2")
export const testingResetFirebaseV2 = httpsCallable(functionsV2, "testing-testingResetFirebaseV2")
export const testingCreatePostsV2 = httpsCallable(functionsV2, "testing-testingCreatePostsV2")
export const adminGeneratePriceScore = httpsCallable(functionsV2, "adminposts-adminGeneratePriceScore")
export const adminGetDescriptions = httpsCallable(functionsV2, "admindescriptions-adminGetDescriptions")
export const getModelTreeV2 = httpsCallable(functionsV2, "search-getModelTreeV2")
export const adminUpdateDescriptions = httpsCallable(functionsV2, "admindescriptions-adminUpdateDescriptions")
export const adminGetCarDescription = httpsCallable(functionsV2, "admindescriptions-adminGetCarDescription")
export const adminGetPostColumnsSettings = httpsCallable(functionsV2, "adminposts-adminGetPostColumnsSettings")
export const adminSavePostColumnsSettings = httpsCallable(functionsV2, "adminposts-adminSavePostColumnsSettings")
export const adminAnswerQuestionV2 = httpsCallable(functionsV2, "adminposts-adminAnswerQuestionV2")

export const adminGetWhatsAppTemplates = httpsCallable(functionsV2, "admincrm-adminGetWhatsAppTemplates")
export const adminSendWhatsApp = httpsCallable(functionsV2, "admincrm-adminSendWhatsApp")
export const adminCreateFakeBid = httpsCallable(functionsV2, "adminposts-adminCreateFakeBid")

export const adminGetDictionaries = httpsCallable(functionsV2, "admindictionaries-adminGetDictionaries")
export const adminSaveDictionaries = httpsCallable(functionsV2, "admindictionaries-adminSaveDictionaries")
export const adminGetNotificationTemplates = httpsCallable(
	functionsV2,
	"admindictionaries-adminGetNotificationTemplates"
)
export const adminSaveNotificationTemplates = httpsCallable(
	functionsV2,
	"admindictionaries-adminSaveNotificationTemplates"
)
